import type { IFuseOptions } from 'fuse.js';

import { useDebounceFn } from '@vueuse/core';
import Fuse from 'fuse.js';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

import type { RoutingOptions } from '@/types/configuration';
import type { Filters } from '@/types/filters';
import type { GTMContext, GTMEvent, GTMEventEcommerce } from '@/types/gtm.js';
import type { LocalizedValue, Product } from '@/types/products';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

function isEcoFriendly(product: Product): boolean {
  if (!product)
    return false;
  const extraProperties = product.properties.find(
    prop => prop.propertyName === 'productEnrichmentByDistributor',
  )?.value?.extra_properties;

  return extraProperties?.some(
    extraProp => extraProp.slug === 'eco-friendly' && extraProp.value,
  ) ?? false;
}

function isProducedInBrittany(product: Product): boolean {
  if (!product)
    return false;
  const extraProperties = product.properties.find(
    prop => prop.propertyName === 'productEnrichmentByDistributor',
  )?.value?.extra_properties;

  return extraProperties?.some(
    extraProp => extraProp.slug === 'produit-en-bretagne' && extraProp.value,
  ) ?? false;
}

function convertUrlToResizer(imageURL: string, width: number = 1024, height: number = 780) {
  const encodedLink = btoa(imageURL.includes('open-system') ? imageURL : imageURL.split('?')[0]);
  const convertionType = 'cropratioresize';
  const quality = 80;
  return `https://api.cloudly.space/resize/${convertionType}/${width}/${height}/${quality}/${encodedLink}/image.webp`;
}

function formatActivityDuration(minutes: number) {
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { currentLocale } = storeToRefs(globalConfigurationStore);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const duration = {
    hours,
    minutes: remainingMinutes,
  };

  // NOTE: INVALID API IN MULTIPLE BROWSERS CAUSE TOO RECENT. CAUSE DISPLAY BUGS
  // if (typeof Intl.DurationFormat === 'function') {
  //   return new Intl.DurationFormat(currentLocale.value, { style: "long" }).format(duration);
  // }

  switch (currentLocale.value) {
    case 'fr-FR':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'heure')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minute')}` : ''}`;
    case 'es-ES':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'hora')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minuto')}` : ''}`;
    case 'de-DE':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'stunde')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minute')}` : ''}`;
    case 'en-US':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'hour')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minute')}` : ''}`;
    default:
      console.error('Invalid locale definition');
      return '';
  }
}

function formatDisplayRating(rating: number): string {
  if (Number.isNaN(rating))
    return '-';
  const roundedString = rating.toFixed(1);
  return roundedString.endsWith('.0') ? roundedString.slice(0, -2) : roundedString;
}

function formatPrice(price: number, currency: 'EUR' | 'USD' = 'EUR', showCents: boolean = true) {
  const locale = currency === 'EUR' ? 'fr-FR' : 'en-US';
  const options: Intl.NumberFormatOptions = { style: 'currency', currency };
  if (!showCents) {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
  }
  return new Intl.NumberFormat(locale, options).format(price);
}

function convertOctoPrice(price: number, currencyPrecision: number): number {
  return price / (10 ** currencyPrecision);
}

function pluralize(quantity: number, text: string) {
  return quantity > 1 ? `${text}s` : text;
}

function formatSpokenLanguages(languages: string[]): string {
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { currentLocale } = storeToRefs(globalConfigurationStore);
  const displayer = new Intl.DisplayNames([currentLocale.value], { type: 'language' });

  return languages
    .reduce((acc, curr) => {
      if (curr === currentLocale.value)
        return [curr, ...acc];
      return [...acc, curr];
    }, [] as string[])
    .map((lang: string) => {
      const value = displayer.of(lang.split('-')[0]);
      return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
    })
    .join(', ');
}

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    dataLayer?: object[];
  }
}

function pushEvent(gtmEvent: GTMEvent) {
  window.dataLayer = (window.dataLayer !== undefined) ? window.dataLayer : [];
  const layer = {
    event: `youbook_${gtmEvent.event}`,
    data: {
      ga4: {
        enable: true,
        label: gtmEvent.label,
        name: gtmEvent.name,
      },
    },
  };
  if (gtmEvent.data) {
    // @ts-expect-error youbook property is undefined in layer type
    layer.data.youbook = gtmEvent.data;
  }

  // Push to GTM
  if (layer.data.ga4 !== undefined) {
    window.dataLayer.push(layer);
  }
}
function pushEventEcommerce(gtmEventEcommerce: GTMEventEcommerce) {
  window.dataLayer = (window.dataLayer !== undefined) ? window.dataLayer : [];
  const layer = {
    event: `${gtmEventEcommerce.event}`,
    ecommerce: gtmEventEcommerce.ecommerce,
    /* data: {
      ga4: {
        enable: true,
        label: gtmEvent.label,
        name: gtmEvent.name,
      },
    }, */
  };

  // Push to GTM
  window.dataLayer.push(layer);
}

function pushEventFromPostMessage(message: MessageEvent) {
  if (message.data?.type !== 'yb-widget-gtm-event') {
    return;
  }
  // Push to GTM
  window.dataLayer?.push(message.data.gtmEvent);
}

function pushContext(gtmContext: GTMContext) {
  window.dataLayer = (window.dataLayer !== undefined) ? window.dataLayer : [];
  const layer = {
    event: `youbook_${gtmContext.event}`,
    data: {
      youbook: gtmContext.data,
    },
  };

  // Push to GTM
  if (gtmContext.data !== undefined) {
    window.dataLayer.push(layer);
  }
}

function getTranslatedName(string: any) {
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { currentLocale } = storeToRefs(globalConfigurationStore);
  return string.translatedName.find((it: any) => it.lang === currentLocale.value)?.value ?? string.name;
}

function useFuseLandingSearch(
  dataSource: () => Array<RoutingOptions>,
  options: IFuseOptions<RoutingOptions> = {
    keys: ['title'],
    threshold: 0.4,
    ignoreLocation: true,
  },
  maxResults?: number,
) {
  const fuse = ref<Fuse<RoutingOptions>>();
  const matchingResults = ref<Array<RoutingOptions>>([]);

  watch(dataSource, (newVal) => {
    if (newVal?.length) {
      fuse.value = new Fuse(newVal, options);
    }
  }, { immediate: true });

  function search(input: string, isFocused: boolean) {
    const search = input.trim();
    if (search.length < 3 || !isFocused || !fuse.value) {
      matchingResults.value = [];
      return;
    }
    const results = fuse.value.search(search);
    matchingResults.value = maxResults ? results.slice(0, maxResults).map(result => result.item) : results.map(result => result.item);
  }

  const debouncedSearch = useDebounceFn(search, 200);

  return {
    fuse,
    matchingResults,
    search,
    debouncedSearch,
  };
}

function hasActiveFilters(filters: Filters): boolean {
  const hasSearch = filters.sorts.some(sort => sort.type === 'search');

  const hasUserFilters = filters.filters.some(filter =>
    ['date_range', 'priceFrom', 'sessionDuration', 'timeRange'].includes(filter.filterProperties.type),
  );

  const hasSelections = filters.selections.some((selection) => {
    if (!['priceFrom', 'sessionDuration', 'timeRange', 'taxonomyPlaces', 'taxonomyThemes', 'taxonomyTypes', 'product_enrichment_taxonomy'].includes(selection.filterProperties?.type)) {
      return false;
    }

    if (!selection.selection) {
      return false;
    }

    // For array selections (themes, types, places)
    if (Array.isArray(selection.selection)) {
      return selection.selection.length > 0;
    }

    // For object selections (priceFrom, timeRange, etc.)
    if (typeof selection.selection === 'object') {
      return Object.keys(selection.selection).length > 0;
    }

    return false;
  });

  return hasSearch || hasUserFilters || hasSelections;
}

function getLocalizedLabel(
  labels: Array<LocalizedValue>,
  fallbackLabel: string,
  locale: string,
): string {
  if (labels.length === 0)
    return fallbackLabel;
  const localeMatch = labels.find(localizedValue => localizedValue.lang === locale);
  return localeMatch?.value ?? labels[0]?.value ?? fallbackLabel;
}

function goToRoute(url: string) {
  window.location.href = url;
}

export {
  convertOctoPrice,
  convertUrlToResizer,
  formatActivityDuration,
  formatDisplayRating,
  formatPrice,
  formatSpokenLanguages,
  getLocalizedLabel,
  getTranslatedName,
  goToRoute,
  hasActiveFilters,
  isEcoFriendly,
  isProducedInBrittany,
  pushContext,
  pushEvent,
  pushEventEcommerce,
  pushEventFromPostMessage,
  useFuseLandingSearch,
};
